import React, { JSX } from "react";
import { ClaimsActivityLineOfBusinessTypeEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import {
  ProviderClaimAmounts,
  ProviderTopPrescriptions,
} from "@app/projects/servicing-provider";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";
import InfoIndicator from "@app/shared/InfoIndicator";
import ProviderClaimVolumes from "@app/projects/servicing-provider/provider-claims/ProviderClaimVolumes";
import LineOfBusinessPayerMix from "@app/projects/servicing-provider/provider-claims/LineOfBusinessPayerMix";
import ProviderNewPatientCPT from "@app/projects/servicing-provider/provider-claims/ProviderNewPatientCPT";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import SpecialtyPredictionModel from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModel";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail";

interface ClaimsProviderInsightsProps {
  record: ServicingProviderDetail;
  showVA: boolean;
  showTricare: boolean;
}

/**
 * Provider Insights Claims Component
 */
const ClaimsProviderInsights = (props: ClaimsProviderInsightsProps): JSX.Element => {

  const featureFlags = React.useContext(FeatureFlagContext).enabledFeatureFlags;
  const renderInfoIndicator = (definition: string | JSX.Element): JSX.Element => {
    return (
      <>
        <span>&nbsp;</span>
        <InfoIndicator className={"informational-indicator"} tooltipTitle={definition} />
      </>
    );
  }

  const specialtyPredictionColumnClass = featureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL)
    ? "data-box col-12 col-sm-3"
    : "data-box col-12 col-sm-6";

  return (
    <React.Fragment>
      <div className="row">
        <div className="data-box col-12 col-sm-6">
          <div className="data-box-header">
            Effectiveness (Quality)
            {renderInfoIndicator("Effectiveness is a measure of quality where 5 is the best score (higher quality) and 1 is the worst score (lower quality). Scores are relative to other providers in the same specialty and state and are derived from claims insight into 1) Mortality 2) Complications 3) Days in acute care")}
          </div>
          <div className="data-box-content">
            {props.record?.effectivenessRank ? `${props.record.effectivenessRank}/5` : "No Data is Available"}
          </div>
        </div>

        <div className={specialtyPredictionColumnClass}>
          <div className="data-box-header">
            Efficiency (Cost)
            {renderInfoIndicator("Efficiency is a measure of physician cost vs. peers where 5 is the best score (lower cost) and 1 is the worst score (higher cost).  Efficiency measures are based on 1) cost per visit 2) cost per patient.  Standardized pricing methodology is used, meaning efficiency is a measure of comparative utilization intensity and not rate/price differences")}
          </div>
          <div className="data-box-content">
            {props.record?.efficiencyRank ? `${props.record.efficiencyRank}/5` : "No Data is Available"}
          </div>
        </div>

        {featureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL) ? (
          <SpecialtyPredictionModel
              className={specialtyPredictionColumnClass}
              specialtyPredictionModel={props.record?.specialtyPredictionModel}
              testId="servicing-provider-profile__claims-provider-insights-specialty-prediction-model"
            />
        ) : null}
      </div>

      <div className="row">
        <div className="data-box col-12 col-sm-6 align-top">
          <div className="data-box-header">Top Conditions</div>
          <div className="data-box-content">
            {props.record?.topConditionsDisplay?.length ? props.record.topConditionsDisplay.map((tc, index) => (
              <EllipsisTooltip
                key={index}
                text={tc}
              />

            )) : "No Data is Available"}
          </div>
        </div>

        <div className="data-box col-12 col-sm-6 align-top">
          <div className="data-box-header">Top Procedures</div>
          <div className="data-box-content">
            {props.record?.topProceduresDisplay?.length ? props.record.topProceduresDisplay.map((tp, index) => (
              <EllipsisTooltip
                key={index}
                text={tp}
              />
            )) : "No Data is Available"}
          </div>
        </div>
      </div>

      <div className="row">
        {
          props.record?.topPrescriptionsDisplay !== undefined &&
          (
            <ProviderTopPrescriptions
              className="col-12 col-sm-6 col-lg-3"
              topPrescriptions={props.record?.topPrescriptionsDisplay}
              testId="servicing-provider-profile__claims-provider-insights-top-prescriptions"
            />
          )
        }
        <ProviderClaimVolumes
          className="col-12 col-sm-6 col-lg-3"
          claimVolumes={props.record}
          showVA={props.showVA}
          showTricare={props.showTricare}
          testId="servicing-provider-profile__claims-provider-insights-claim-volumes"
        />
        <ProviderClaimAmounts
          className="col-12 col-sm-6 col-lg-3"
          claimAmounts={props.record}
          showVA={props.showVA}
          showTricare={props.showTricare}
          testId="servicing-provider-profile__claims-provider-insights-claim-amounts"
        />
        
        <ProviderNewPatientCPT
          className="col-12 col-sm-6 col-lg-3"
          newPatientIndicators={props.record}
          showVA={props.showVA}
          showTricare={props.showTricare}
          testId="servicing-provider-profile__claims-provider-insights-new-patient-cpt"
        />

      </div>

      <div className="row">
        <div className="data-box col-12 mt-2">
          <div className="data-box-header">
            Payer Mix <InfoIndicator className={"informational-indicator"} tooltipTitle="The breakdown of provider claim volume by payer for each line of business." />
          </div>
        </div>
      </div>

      <div className="row servicing-provider-profile__payer-mix">
          <LineOfBusinessPayerMix
            className="col-12 col-lg-6 col-xl-4 align-top"
            lineOfBusiness={ClaimsActivityLineOfBusinessTypeEnum.Total}
            participationDisplay={props.record?.totalParticipationDisplay}
            testId="servicing-provider-profile__payer-mix-Total-0"
          />

          <LineOfBusinessPayerMix
            className="col-12 col-lg-6 col-xl-4 align-top"
            lineOfBusiness={ClaimsActivityLineOfBusinessTypeEnum.Medicare}
            participationDisplay={props.record?.medicareParticipationDisplay}
            testId="servicing-provider-profile__payer-mix-Medicare-1"
          />

          <LineOfBusinessPayerMix
            className="col-12 col-lg-6 col-xl-4 align-top"
            lineOfBusiness={ClaimsActivityLineOfBusinessTypeEnum.Medicaid}
            participationDisplay={props.record?.medicaidParticipationDisplay}
            testId="servicing-provider-profile__payer-mix-Medicaid-2"
          />

          <LineOfBusinessPayerMix
            className="col-12 col-lg-6 col-xl-4 align-top"
            lineOfBusiness={ClaimsActivityLineOfBusinessTypeEnum.Commercial}
            participationDisplay={props.record?.commercialParticipationDisplay}
            testId="servicing-provider-profile__payer-mix-Commercial-3"
          />

        {props.showVA ? (
            <LineOfBusinessPayerMix
              className="col-12 col-lg-6 col-xl-4 align-top"
              lineOfBusiness={ClaimsActivityLineOfBusinessTypeEnum.VA}
              participationDisplay={props.record?.vaParticipationDisplay}
              testId="servicing-provider-profile__payer-mix-VA-4"
            />
        ) : undefined}

        {props.showTricare ? (
            <LineOfBusinessPayerMix
              className="col-12 col-lg-6 col-xl-4 align-top"
              lineOfBusiness={ClaimsActivityLineOfBusinessTypeEnum.TRICARE}
              participationDisplay={props.record?.tricareParticipationDisplay}
              testId="servicing-provider-profile__payer-mix-TRICARE-5"
            />
        ) : undefined}
      </div>

    </React.Fragment>
  )
}

export default ClaimsProviderInsights;
